import type { RunEvent, RunInfo } from "../../lib/productsApi";
import { clock, utcMs } from "./text";

/**
 * A run's log in plain words, built from its events the way the prototype shows a run: every tool call with
 * what it did, the agent's own thoughts trimmed, notes and errors. The raw input and output stay in Details.
 */
export type LogKind = "call" | "think" | "note" | "err";

export interface LogEntry {
  key: string;
  at: string;
  /** Time since the run started, "0:24". */
  t: string;
  k: LogKind;
  tool?: string;
  x: string;
  r?: string;
  i?: unknown;
  o?: unknown;
}

/** What a tool did, as the owner reads it. Unknown tools show their name in words. */
const str = (v: unknown) => (typeof v === "string" ? v : "");
// Why: a run reads its skills and tools as files named by slug; the owner sees their names.
const NAMES: Record<string, string> = { "meta-ads": "Meta ads", "google-ads": "Google Ads", "linkedin-ads": "LinkedIn Ads" };
const itemName = (slug: string) => NAMES[slug] ?? slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, " ");

const hostOf = (u: string) => u.replace(/^https?:\/\/(www\.)?/, "").split(/[/?#]/)[0];

export function toolLine(name: string, args: Record<string, unknown>): string {
  if (name === "read") {
    const p = str(args.path);
    if (p.endsWith("skills/playbook/SKILL.md")) return "Read the playbook";
    const skill = p.match(/skills\/([^/]+)\/SKILL\.md$/);
    if (skill) return `Read ${itemName(skill[1])}`;
    if (/(^|\/)product\.md$/.test(p)) return "Read the product profile";
    if (/(^|\/)goal\.json$/.test(p)) return "Read the goal";
    if (/(^|\/)MATERIAL\.md$/.test(p)) return "Read what you told us";
    return `Read ${p}`;
  }
  if (name === "list_lessons") return "Read the accepted lessons";
  if (name === "web_search") return `Searched: ${str(args.query)}`;
  if (name === "read_page") return `Read a page: ${hostOf(str(args.url))}`;
  if (name === "find_tool") return `Looked for a tool: ${str(args.job)}`;
  if (name === "call_catalog_tool") return "Looked up a data source";
  if (name === "modelmatch_lookup") return "Looked up sales records";
  if (name === "save_plan") return "Saved the plan for your review";
  if (name === "save_playbook") return "Saved the playbook for your review";
  if (name === "goal_state") return "Read the goal, its plan and its rules";
  if (name === "results_by_ad") return "Read results per ad";
  if (name === "tracking_check") return "Checked tracking";
  if (name === "email_replies") return "Read new replies";
  if (name === "list_leads") return "Read the people list";
  if (name === "propose_action") return "Asked you before acting";
  if (name === "propose_change") return "Proposed a change to the plan";
  if (name === "propose_lesson") return "Proposed a lesson";
  if (name === "save_review") return "Saved the review";
  if (name === "answer") return "Answered you";
  return name.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/** Owner-facing tool names for a step's "How" (the prototype's PT). */
const PT: Record<string, string> = {
  read: "Read",
  web_search: "Web search",
  read_page: "Read a page",
  find_tool: "Find a tool",
  call_catalog_tool: "Data source",
  modelmatch_lookup: "Sales records",
  list_lessons: "Lessons",
  save_plan: "The plan",
  save_playbook: "The playbook",
  goal_state: "The goal",
  results_by_ad: "Results per ad",
  tracking_check: "Tracking",
  email_replies: "Replies",
  list_leads: "People list",
  propose_action: "Held for you",
  propose_change: "A change",
  save_review: "The review",
};
export const toolName = (t: string) => PT[t] ?? t.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

const THOUGHT_MAX = 600;
const trim = (s: string) => {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length > THOUGHT_MAX ? `${t.slice(0, THOUGHT_MAX).trimEnd()}…` : t;
};

export function buildLog(run: Pick<RunInfo, "started_at" | "created_at"> | null, events: RunEvent[]): LogEntry[] {
  const start = utcMs(run?.started_at) || utcMs(events[0]?.at) || utcMs(run?.created_at) || 0;
  const since = (at: string) => clock((utcMs(at) || start) - start);
  const out: LogEntry[] = [];
  const calls = new Map<string, LogEntry>();
  for (const e of events) {
    const d = e.data;
    if (e.type === "tool.call") {
      const name = str(d.name);
      const args = (d.args && typeof d.args === "object" ? d.args : {}) as Record<string, unknown>;
      const row: LogEntry = { key: `e${e.seq}`, at: e.at, t: since(e.at), k: "call", tool: name, x: toolLine(name, args), i: args };
      calls.set(str(d.id), row);
      out.push(row);
    } else if (e.type === "tool.result") {
      const row = calls.get(str(d.id));
      if (!row) continue;
      row.o = d.preview ?? "";
      if (d.isError) {
        row.k = "err";
        row.r = "failed";
      } else if (row.tool === "save_plan" || row.tool === "save_playbook") row.r = "held for you";
    } else if ((e.type === "reasoning" || e.type === "text") && str(d.text).trim()) {
      out.push({ key: `e${e.seq}`, at: e.at, t: since(e.at), k: "think", x: trim(str(d.text)) });
    } else if (e.type === "note" && str(d.text)) {
      out.push({ key: `e${e.seq}`, at: e.at, t: since(e.at), k: "note", x: str(d.text) });
    } else if (e.type === "error") {
      out.push({ key: `e${e.seq}`, at: e.at, t: since(e.at), k: "err", x: str(d.message) || "The run hit an error." });
    }
  }
  return out;
}

/** The prototype's run footer counts. */
export function runCounts(run: RunInfo | null, log: LogEntry[]) {
  const calls = log.filter((e) => e.tool).length;
  const thoughts = log.filter((e) => e.k === "think").length;
  const secs = run?.started_at && run.ended_at ? Math.max(0, Math.round((utcMs(run.ended_at) - utcMs(run.started_at)) / 1000)) : null;
  return { calls, thoughts, secs };
}
